import { useEffect, useState } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import type { ForecastSnapshotSiteRow } from '../../../lib/api/platformSettings';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CalendarRange, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function clampedInt(value: string, min: number, max: number): number | null {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n) || n < min || n > max) return null;
  return n;
}

interface WeekdayHourInputsProps {
  weekday: number;
  hour: number;
  disabled: boolean;
  onSave: (patch: { forecast_snapshot_weekday?: number; forecast_snapshot_hour?: number }) => void;
}

function WeekdayHourInputs({ weekday, hour, disabled, onSave }: WeekdayHourInputsProps) {
  // Uncontrolled inputs keyed on value + disabled, so a landed save or a
  // toggled master switch remounts them (same pattern as the arrears section).
  return (
    <div className={`flex items-center gap-2 ${disabled ? 'opacity-50' : ''}`}>
      <select
        key={`w-${weekday}-${disabled}`}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        defaultValue={weekday}
        disabled={disabled}
        aria-label="Capture weekday"
        onChange={(e) => {
          const v = clampedInt(e.target.value, 0, 6);
          if (v !== null && v !== weekday) onSave({ forecast_snapshot_weekday: v });
        }}
      >
        {WEEKDAYS.map((name, i) => <option key={name} value={i}>{name}</option>)}
      </select>
      <span className="text-xs text-muted-foreground">at</span>
      <Input
        key={`h-${hour}-${disabled}`}
        type="number" min={0} max={23} className="w-16" defaultValue={hour}
        disabled={disabled}
        aria-label="Capture hour"
        onBlur={(e) => {
          const v = clampedInt(e.target.value, 0, 23);
          if (v === null) {
            toast.error('Hour must be between 0 and 23');
            e.target.value = String(hour);
            return;
          }
          if (v !== hour) onSave({ forecast_snapshot_hour: v });
        }}
      />
      <span className="text-xs text-muted-foreground">:00</span>
    </div>
  );
}

/**
 * Platform-admin schedule for the weekly forecast copy behind the PO Line
 * Dashboard's Forecast Stability sub-tab: per customer site, opt in plus the
 * weekday/hour on the site's own clock (the legacy portal's "cache day").
 */
export function ForecastSnapshotScheduleSection() {
  const [sites, setSites] = useState<ForecastSnapshotSiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getForecastSnapshotConfig();
        setSites(data.sites);
      } catch (err: unknown) {
        console.error('Error loading forecast snapshot config:', err);
        toast.error('Failed to load the forecast copy schedules');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveSite = async (site: ForecastSnapshotSiteRow, patch: Partial<ForecastSnapshotSiteRow>) => {
    setSaving(true);
    try {
      const { data } = await platformSettingsAPI.updateForecastSnapshotSite({
        company_id: site.company_id, ...patch,
      });
      setSites((current) => current.map((s) => (s.company_id === site.company_id ? data : s)));
      toast.success(`Saved ${site.company_name}`);
    } catch (err: unknown) {
      console.error('Error saving forecast copy schedule:', err);
      toast.error(`Failed to save ${site.company_name}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarRange className="h-5 w-5" />
          Forecast Copy Schedule
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When each site&apos;s live forecast is copied for the Forecast Stability
          chart. The chart compares the live forecast with the latest copy taken
          before the current forecast import, so the day chosen defines what
          &quot;since last time&quot; means for that site. Pick a weekday other than the day
          the forecast feed lands: a copy on the import day is treated as this import&apos;s own
          state, so the comparison would reach back a further week. Applies to PO-line-grain
          sites; schedule-line sites are copied through the Forecast tab snapshot instead.
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead>Copy weekly</TableHead>
              <TableHead>Schedule (site-local)</TableHead>
              <TableHead>Runs at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sites.map((site) => (
              <TableRow key={site.company_id}>
                <TableCell className="font-medium">{site.company_name}</TableCell>
                <TableCell>
                  <Switch
                    checked={site.capture_forecast_snapshots}
                    disabled={saving}
                    aria-label={`Copy weekly for ${site.company_name}`}
                    onCheckedChange={(checked) => saveSite(site, { capture_forecast_snapshots: checked })}
                  />
                </TableCell>
                <TableCell>
                  <WeekdayHourInputs
                    weekday={site.forecast_snapshot_weekday}
                    hour={site.forecast_snapshot_hour}
                    disabled={saving || !site.capture_forecast_snapshots}
                    onSave={(patch) => saveSite(site, patch)}
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {site.capture_forecast_snapshots
                    ? `${WEEKDAYS[site.forecast_snapshot_weekday] ?? '?'}s, ${String(site.forecast_snapshot_hour).padStart(2, '0')}:00 site-local`
                    : 'copy off'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {sites.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No customer sites found.</p>
        )}
      </CardContent>
    </Card>
  );
}
